import { c as _c } from "react/compiler-runtime";
import type { TenantRoleKindDiscriminant } from '@onefootprint/request-types/dashboard';
import { Checkbox, Tooltip } from '@onefootprint/ui';
import { Fragment } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { DecryptOption } from '@/components/roles';
import { useDecryptOptions } from '@/components/roles';
import { getCoveringOption } from '@/components/roles/utils/decrypt-access';
import type { Access } from '@/components/roles/utils/scope-environments';
import type { FormData } from '../role-form/role-form.types';
type DecryptTableProps = {
  kind: TenantRoleKindDiscriminant;
};
type CellProps = {
  label: string;
  checked: boolean;
  lockedReason?: string;
  onChange: (checked: boolean) => void;
};
const Cell = t0 => {
  const $ = _c(13);
  const {
    label,
    checked,
    lockedReason,
    onChange
  } = t0;
  const t1 = !lockedReason;
  let t2;
  if ($[0] !== label) {
    t2 = <span className="sr-only">{label}</span>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = !!lockedReason;
  let t4;
  if ($[2] !== onChange) {
    t4 = event => onChange(event.target.checked);
    $[2] = onChange;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== checked || $[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = <Checkbox label={t2} checked={checked} disabled={t3} onChange={t4} />;
    $[4] = checked;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== lockedReason || $[10] !== t1 || $[11] !== t5) {
    t6 = <td className="w-24 border-t border-tertiary px-3"><div className="flex justify-center"><Tooltip content={lockedReason} disabled={t1}>{t5}</Tooltip></div></td>;
    $[9] = lockedReason;
    $[10] = t1;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
const DecryptTable = t0 => {
  const $ = _c(56);
  const {
    kind
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "form"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("roles", t1);
  const {
    options: groups,
    allOption,
    allExceptPciDataOption,
    getLabel
  } = useDecryptOptions();
  let t2;
  if ($[1] !== t) {
    t2 = {
      name: "decryptAccess",
      rules: {
        validate: value => Object.values(value).some(_temp) || t(_temp2)
      }
    };
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    field,
    fieldState
  } = useController(t2);
  const access = field.value;
  const isApiKey = kind === "api_key";
  let t3;
  if ($[3] !== access || $[4] !== field) {
    t3 = (option, optionAccess_0) => {
      field.onChange({
        ...access,
        [option]: optionAccess_0
      });
    };
    $[3] = access;
    $[4] = field;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const setAccess = t3;
  let t4;
  if ($[6] !== getLabel || $[7] !== t) {
    t4 = covering => covering && t(_temp3, {
      option: getLabel(covering)
    });
    $[6] = getLabel;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const includedIn = t4;
  let t5;
  if ($[9] !== access || $[10] !== includedIn || $[11] !== isApiKey || $[12] !== setAccess || $[13] !== t) {
    t5 = t6 => {
      const {
        value: option_0,
        label
      } = t6;
      const optionAccess_1 = access[option_0] ?? "none";
      const sandboxCovering = getCoveringOption(access, option_0, "sandbox_only");
      const productionCovering = getCoveringOption(access, option_0, "sandbox_and_production");
      const isInProduction = optionAccess_1 === "sandbox_and_production" || !!productionCovering;
      return <tr key={option_0} className="border-t border-tertiary"><th scope="row" className="border-t border-tertiary px-3 py-2 text-left text-body-3 text-primary">{label}</th>{isApiKey ? <Cell label={label} checked={isInProduction} lockedReason={includedIn(productionCovering)} onChange={checked => setAccess(option_0, checked ? "sandbox_and_production" : "none")} /> : <><Cell label={t(_temp4, {
            field: label
          })} checked={optionAccess_1 !== "none" || !!sandboxCovering} lockedReason={includedIn(sandboxCovering) ?? (isInProduction ? t(_temp5) : undefined)} onChange={checked_0 => setAccess(option_0, checked_0 ? "sandbox_only" : "none")} /><Cell label={t(_temp6, {
            field: label
          })} checked={isInProduction} lockedReason={includedIn(productionCovering)} onChange={checked_1 => setAccess(option_0, checked_1 ? "sandbox_and_production" : "sandbox_only")} /></>}</tr>;
    };
    $[9] = access;
    $[10] = includedIn;
    $[11] = isApiKey;
    $[12] = setAccess;
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const renderRow = t5;
  let t6;
  if ($[15] !== isApiKey || $[16] !== t) {
    t6 = !isApiKey && <p className="text-body-3 text-tertiary">{t(_temp7)}</p>;
    $[15] = isApiKey;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t) {
    t7 = t(_temp8);
    $[18] = t;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t) {
    t8 = t(_temp9);
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = <th scope="col" className="sticky top-0 z-1 bg-secondary px-3 py-2 text-left">{t8}</th>;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== isApiKey || $[25] !== t) {
    t10 = isApiKey ? <th scope="col" className="sticky top-0 z-1 bg-secondary px-3 py-2">{t(_temp10)}</th> : <><th scope="col" className="sticky top-0 z-1 bg-secondary px-3 py-2">{t(_temp11)}</th><th scope="col" className="sticky top-0 z-1 bg-secondary px-3 py-2">{t(_temp12)}</th></>;
    $[24] = isApiKey;
    $[25] = t;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t9) {
    t11 = <thead><tr className="text-caption-2 text-tertiary">{t9}{t10}</tr></thead>;
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== allExceptPciDataOption || $[31] !== allOption || $[32] !== renderRow) {
    t12 = [allOption, allExceptPciDataOption].map(renderRow);
    $[30] = allExceptPciDataOption;
    $[31] = allOption;
    $[32] = renderRow;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== groups || $[35] !== isApiKey || $[36] !== renderRow) {
    let t14;
    if ($[38] !== isApiKey || $[39] !== renderRow) {
      t14 = group => <Fragment key={group.label}><tr><th scope="colgroup" colSpan={isApiKey ? 2 : 3} className="bg-secondary px-3 py-2 text-left text-caption-2 text-tertiary">{group.label}</th></tr>{group.options.map(renderRow)}</Fragment>;
      $[38] = isApiKey;
      $[39] = renderRow;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    t13 = groups.map(t14);
    $[34] = groups;
    $[35] = isApiKey;
    $[36] = renderRow;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[41] !== t12 || $[42] !== t13) {
    t14 = <tbody>{t12}{t13}</tbody>;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t11 || $[45] !== t14) {
    t15 = <table className="w-full border-separate border-spacing-0">{t11}{t14}</table>;
    $[44] = t11;
    $[45] = t14;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== t15 || $[48] !== t7) {
    t16 = <section className="max-h-80 overflow-auto rounded-default border border-tertiary" aria-label={t7}>{t15}</section>;
    $[47] = t15;
    $[48] = t7;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== fieldState.error) {
    t17 = fieldState.error && <p className="text-body-3 text-error">{fieldState.error.message}</p>;
    $[50] = fieldState.error;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t16 || $[53] !== t17 || $[54] !== t6) {
    t18 = <div className="flex flex-col gap-2">{t6}{t16}{t17}</div>;
    $[52] = t16;
    $[53] = t17;
    $[54] = t6;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  return t18;
};
export default DecryptTable;
function _temp(optionAccess) {
  return optionAccess && optionAccess !== "none";
}
function _temp2($) {
  return $.decrypt.errors.required;
}
function _temp3($_0) {
  return $_0.decrypt.table.includedIn;
}
function _temp4($_1) {
  return $_1.decrypt.table.inSandbox;
}
function _temp5($_2) {
  return $_2.decrypt.table.allowedInProduction;
}
function _temp6($_3) {
  return $_3.decrypt.table.inProduction;
}
function _temp7($_4) {
  return $_4.decrypt.table.environmentHint;
}
function _temp8($_5) {
  return $_5.decrypt.label;
}
function _temp9($_6) {
  return $_6.decrypt.table.data;
}
function _temp10($_7) {
  return $_7.access.allow;
}
function _temp11($_8) {
  return $_8.decrypt.table.sandbox;
}
function _temp12($_9) {
  return $_9.decrypt.table.production;
}